import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

// Der Maps-Key wird serverseitig pro Request als data-Attribut gesetzt
// (includes/MapsRuntime.php) und erst im Browser gelesen.
function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement) {
    return "";
  }

  const key = document.documentElement.getAttribute("data-maps-geocoder-key");
  return key ? key.trim() : "";
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

function validLatLon(lat, lon) {
  return isFiniteNumber(lat) && isFiniteNumber(lon)
    && lat >= -90 && lat <= 90
    && lon >= -180 && lon <= 180;
}

function str(v) {
  if (typeof v === "string") {
    const s = v.trim();
    return s ? s : null;
  }
  if (typeof v === "number" && isFinite(v)) {
    return String(v);
  }
  return null;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.requestId = 0;
  }

  // Bricht die laufende Suche ab und invalidiert verspätete Antworten.
  cancelAutocomplete() {
    this.requestId++;

    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Wandelt ein Feature in den bisherigen Aufrufervertrag um
  // (street, number, zip, city, district, lat, lng, state, country, text).
  // Das Feature selbst wird nicht verändert.
  _geoaddress(l) {
    if (!l || typeof l !== "object") {
      return null;
    }

    const props = l.properties && typeof l.properties === "object" ? l.properties : {};
    const coords = l.geometry && Array.isArray(l.geometry.coordinates) ? l.geometry.coordinates : null;

    // Koordinaten sind [lng, lat].
    const lng = coords ? Number(coords[0]) : NaN;
    const lat = coords ? Number(coords[1]) : NaN;
    if (!validLatLon(lat, lng)) {
      return null;
    }

    const type = str(props.type);
    const name = str(props.name);
    const osmKey = str(props.osm_key);
    const osmValue = str(props.osm_value);

    let street = str(props.street);
    let city = str(props.city);
    let district = str(props.district);

    if (type) {
      if (type === "street" && !street) {
        street = name;
      } else if (type === "city" && !city) {
        city = name;
      } else if (type === "district" && !district) {
        district = name;
      }
    } else if (name) {
      // Legacy-Fallback für Antworten ohne properties.type
      if (osmKey === "highway" && !street) {
        street = name;
      } else if ((osmKey === "boundary" || (osmKey === "place" && osmValue === "suburb"))
        && city && name !== city && !district) {
        district = name;
      } else if (osmKey === "place" && !city
        && ["city", "town", "village", "hamlet", "municipality"].indexOf(osmValue) !== -1) {
        city = name;
      }
    }

    if (district && city && district === city) {
      district = null;
    }

    const geoaddress = {};

    if (street) {
      geoaddress.street = street;
      const number = str(props.housenumber);
      if (number) {
        geoaddress.number = number;
      }
    }

    const zip = str(props.postcode);
    if (zip) {
      geoaddress.zip = zip;
    }
    if (city) {
      geoaddress.city = city;
    }
    if (district) {
      geoaddress.district = district;
    }

    geoaddress.lat = lat;
    geoaddress.lng = lng;

    geoaddress.state = str(props.state) || undefined;
    geoaddress.country = str(props.country) || undefined;

    let text = this._geoaddressText(geoaddress);
    if (name && !street && !city && !district) {
      // Ohne Orts-/Straßenangabe den Namen nur als Beschriftung verwenden.
      text = text ? name + ", " + text : name;
    } else if (name && type && ["house", "street", "city", "district"].indexOf(type) === -1
      && text.indexOf(name) === -1) {
      text = name + ", " + text;
    }
    geoaddress.text = text;

    return geoaddress;
  }

  // Liefert die Treffer der jüngsten Suche; abgebrochene oder
  // überholte Anfragen enden mit null statt offen zu bleiben.
  autocomplete(q) {
    this.cancelAutocomplete();
    const requestId = this.requestId;

    const query = typeof q === "string" ? q.trim() : "";
    const key = mapsKey();
    if (!query || !key) {
      return Promise.resolve(query ? [] : null);
    }

    const source = this.axios.CancelToken.source();
    this.axiosRequest = { source };

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (requestId !== this.requestId) {
        return null;
      }

      const features = res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
      const results = [];
      features.forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }).catch(thrown => {
      if (this.axios.isCancel(thrown) || requestId !== this.requestId) {
        return null;
      }

      console.warn("Geocoding fehlgeschlagen");
      return [];
    }).then(results => {
      if (requestId === this.requestId) {
        this.axiosRequest = null;
      }
      return results;
    });
  }

  // Liefert die Adresse zu Koordinaten oder null; ohne Key oder bei
  // ungültigen Koordinaten ohne Anfrage.
  reverse(lat, lng) {
    lat = Number(lat);
    lng = Number(lng);

    const key = mapsKey();
    if (!key || !validLatLon(lat, lng)) {
      return Promise.resolve(null);
    }

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT,
      params: {
        lat: lat,
        lon: lng,
        lang: "de",
        key: key
      }
    }).then(res => {
      if (res && res.data && Array.isArray(res.data.features) && res.data.features.length > 0) {
        return this._geoaddress(res.data.features[0]);
      }
      return null;
    }).catch(() => {
      console.warn("Reverse-Geocoding fehlgeschlagen");
      return null;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    });
  });
}

export { GeocoderFactory };

export default {};
